import type { CalendarItem, CalendarSubscription } from "../api";

export const defaultCalendarSubscriptions: CalendarSubscription[] = [
  { key: "company", color: "#f6bf26", enabled: true }, { key: "me", color: "#0b8043", enabled: true },
];

export const calendarPalette = [
  "#7986cb", "#33b679", "#8e24aa", "#e67c73", "#f6bf26", "#f4511e", "#039be5", "#616161", "#3f51b5", "#0b8043", "#d50000",
];
export const calendarColorNames = ["라벤더", "세이지", "포도", "플라밍고", "바나나", "귤", "공작", "흑연", "블루베리", "바질", "토마토"];

export function nextCalendarColor(subscriptions: CalendarSubscription[]): string {
  // First unused Google event color; only after all 11 are used, reuse the least-used one.
  const counts = calendarPalette.map(color => subscriptions.filter(value => value.color.toLowerCase() === color).length);
  return calendarPalette[counts.indexOf(Math.min(...counts))];
}

// One event, one displayed calendar: company first, then mine, then subscription order.
export function eventSubscription(item: CalendarItem, subscriptions: CalendarSubscription[], userId: string): CalendarSubscription | undefined {
  if (item.companyWide || item.kind === "HOLIDAY") return subscriptions.find(value => value.key === "company" && value.enabled);
  const own = subscriptions.find(value => value.key === "me" && value.enabled && item.participantIds.includes(userId));
  return own ?? subscriptions.find(value => value.enabled && value.key !== "me" && value.key !== "company" && item.participantIds.includes(value.key));
}

export function calendarDate(value: string | number | Date, timeZone = "Asia/Seoul"): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(value));
  return ["year", "month", "day"].map(key => parts.find(part => part.type === key)!.value).join("-");
}

export function shiftDate(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function monthDays(month: string): string[] {
  const first = `${month}-01`;
  const start = shiftDate(first, -new Date(`${first}T00:00:00Z`).getUTCDay());
  return Array.from({ length: 42 }, (_, index) => shiftDate(start, index));
}

export function occursOn(item: CalendarItem, date: string, zone: string): boolean {
  return calendarDate(item.startsAt, zone) <= date && calendarDate(Date.parse(item.endsAt) - 1, zone) >= date;
}

export function calendarItemsByDate(items: CalendarItem[], days: string[], zone: string): Map<string, CalendarItem[]> {
  const result = new Map(days.map(day => [day, [] as CalendarItem[]]));
  for (const item of items) {
    const first = calendarDate(item.startsAt, zone);
    const last = calendarDate(Date.parse(item.endsAt) - 1, zone);
    for (const day of days) if (first <= day && day <= last) result.get(day)!.push(item);
  }
  return result;
}

export function calendarTime(value: string, zone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(value));
}

export function localCalendarTime(value: string, zone: string): string {
  return `${calendarDate(value, zone)}T${calendarTime(value, zone)}`;
}
